<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import type { ActionMenuItem } from '@/services/types/api.types';

// PROPS
const props = defineProps<{
  row: any;
  actionMenuItems: ActionMenuItem[];
}>();

// EMITS
const emit = defineEmits(['actions']); // Single emit

const { t } = useI18n();

function isDisabled(actionMenuItem: ActionMenuItem): boolean {
  if (actionMenuItem.disabled instanceof Function) {
    return actionMenuItem.disabled(props.row);
  }
  return actionMenuItem.disabled ?? false;
}

function tooltipOf(actionMenuItem: ActionMenuItem): string | undefined {
  if (actionMenuItem.tooltip instanceof Function) {
    return actionMenuItem.tooltip(props.row);
  }
  return actionMenuItem.tooltip;
}

// Why a disabled item is refused, when it says. A disabled item takes no focus and a tooltip opens on hover only, so
// the reason shows under its label, for the keyboard too, and a screen reader learns it from the item's name.
function reasonOf(actionMenuItem: ActionMenuItem): string | undefined {
  return isDisabled(actionMenuItem) ? tooltipOf(actionMenuItem) : undefined;
}

function ariaLabelOf(actionMenuItem: ActionMenuItem): string | undefined {
  const reason = reasonOf(actionMenuItem);
  return reason ? t('global.action-blocked', { action: actionMenuItem.label, reason }) : undefined;
}

function triggerAction(action: string, payload?: object) {
  emit('actions', { action, payload });
}
</script>
<template>
  <div class="table-actions-menu">
    <q-btn icon="more_horiz" flat dense>
      <q-menu class="q-actions-menu">
        <q-list>
          <q-item
            v-for="actionMenuItem in props.actionMenuItems"
            clickable
            @click="triggerAction(actionMenuItem.action, { row: props.row })"
            v-close-popup="!isDisabled(actionMenuItem)"
            :disable="isDisabled(actionMenuItem)"
            :aria-label="ariaLabelOf(actionMenuItem)"
            :key="actionMenuItem.action"
          >
            <q-tooltip v-if="!isDisabled(actionMenuItem) && tooltipOf(actionMenuItem)">
              {{ tooltipOf(actionMenuItem) }}
            </q-tooltip>
            <q-item-section>
              <q-item-label>{{ actionMenuItem.label }}</q-item-label>
              <q-item-label v-if="reasonOf(actionMenuItem)" caption class="table-actions-menu__reason">
                {{ reasonOf(actionMenuItem) }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-btn>
  </div>
</template>
<style lang="scss">
.q-actions-menu {
  .q-list {
    min-width: 100px;
    white-space: nowrap;
  }

  .table-actions-menu__reason {
    max-width: 260px;
    white-space: normal;
  }
}
</style>
